class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__footer',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__heading',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* Advent smart cart: celebrate once when the meter reaches a new milestone (free shipping, -10%, -15%).
   The meter lives inside cart-drawer-items and is re-rendered on every cart change, so the level is
   compared across renders here (sessionStorage key adv-cart-level). Confetti goes on .drawer__inner, which survives item re-renders.
   Transform/opacity only; nothing for reduced motion. */
(() => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const KEY = 'adv-cart-level';
  const COLORS = ['#C8322F', '#1F6B45', '#D9A441', '#F6C1C7', '#BFE6D1', '#BFDDF2'];
  const PIECES = 16;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const readLevel = () => {
    const meter = drawer.querySelector('[data-adv-meter]');
    return meter ? Number(meter.dataset.level) || 0 : 0;
  };
  const stored = () => {
    try {
      const v = sessionStorage.getItem(KEY);
      return v === null ? null : Number(v);
    } catch (e) {
      return null;
    }
  };
  const store = (level) => {
    try {
      sessionStorage.setItem(KEY, String(level));
    } catch (e) {
      /* storage blocked: celebration still works for this page view */
    }
  };

  let best = Math.max(stored() || 0, readLevel());
  store(best);

  function confetti(meter) {
    const inner = drawer.querySelector('.drawer__inner');
    if (!inner) return;
    const box = inner.getBoundingClientRect();
    const at = meter.getBoundingClientRect();
    const layer = document.createElement('div');
    layer.className = 'adv-cart-confetti';
    layer.setAttribute('aria-hidden', 'true');
    const top = Math.max(0, at.top - box.top - 40);
    layer.style.top = `${top}px`;
    layer.style.height = `${Math.max(160, at.bottom - box.top - top + 60)}px`;
    for (let i = 0; i < PIECES; i += 1) {
      const piece = document.createElement('i');
      const spread = (i / (PIECES - 1)) * 2 - 1;
      piece.style.setProperty('--x', `${Math.round(spread * 140 + (Math.random() * 30 - 15))}px`);
      piece.style.setProperty('--y', `${Math.round(-30 - Math.random() * 60)}px`);
      piece.style.setProperty('--r', `${Math.round(Math.random() * 540 - 270)}deg`);
      piece.style.setProperty('--d', `${Math.round(Math.random() * 120)}ms`);
      piece.style.setProperty('--c', COLORS[i % COLORS.length]);
      layer.appendChild(piece);
    }
    inner.appendChild(layer);
    setTimeout(() => layer.remove(), 1400);
  }

  function celebrate() {
    const meter = drawer.querySelector('[data-adv-meter]');
    if (!meter) return;
    meter.classList.add('is-celebrating');
    if (!reduced) confetti(meter);
  }

  /* `best` is the highest level seen this session, so each milestone gets one party, not one per toggle. */
  let timer = null;
  function check() {
    const level = readLevel();
    if (level <= best) return;
    if (drawer.classList.contains('active')) celebrate();
    best = level;
    store(best);
  }

  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(check, 160);
  }).observe(drawer, { childList: true, subtree: true });
})();
